import React, { useEffect, useRef } from 'react'
import { trackingState } from './detectors/trackingDetector'
import { drawPaths } from './pathRenderer'
import styles from './Vision.module.css'

// Draw the multi-point crossing PATH on the play screen exactly like the
// Configure Lines editor: move the cursor to preview the next point (low-opacity
// ghost), click empty space to drop a point, drag a point to move it. The line
// is a single-colour gate with direction arrows. Points live in
// trackingState.pathPoints (unmirrored normalized video space) and go through
// the same object-fit:cover + mirror transform as the boxes.
const TrackingControls = ({ videoRef, mirrored }) => {
    const canvasRef = useRef(null)
    const cursorRef = useRef(null)
    const dragRef = useRef(null)
    const rafRef = useRef(null)

    const cover = () => {
        const v = videoRef.current
        if (!v) return null
        const rect = v.getBoundingClientRect()
        const vw = v.videoWidth || rect.width
        const vh = v.videoHeight || rect.height
        const scale = Math.max(rect.width / vw, rect.height / vh)
        const dispW = vw * scale
        const dispH = vh * scale
        return {
            rect,
            dispW,
            dispH,
            offX: (rect.width - dispW) / 2,
            offY: (rect.height - dispH) / 2,
        }
    }
    // Normalized point -> canvas pixels (cover + mirror), matching the boxes.
    const toPx = (n) => {
        const c = cover()
        if (!c) return { x: 0, y: 0 }
        let px = n.x * c.dispW + c.offX
        if (mirrored) px = c.rect.width - px
        return { x: px, y: n.y * c.dispH + c.offY }
    }
    const fromPointer = (clientX, clientY) => {
        const c = cover()
        if (!c) return null
        let sx = clientX - c.rect.left
        const sy = clientY - c.rect.top
        if (mirrored) sx = c.rect.width - sx
        return {
            x: Math.min(1, Math.max(0, (sx - c.offX) / c.dispW)),
            y: Math.min(1, Math.max(0, (sy - c.offY) / c.dispH)),
        }
    }

    // Continuous redraw so the line tracks video load / resize / edits.
    useEffect(() => {
        const draw = () => {
            rafRef.current = requestAnimationFrame(draw)
            const canvas = canvasRef.current
            const v = videoRef.current
            if (!canvas || !v) return
            const rect = v.getBoundingClientRect()
            const dpr = window.devicePixelRatio || 1
            const w = Math.round(rect.width)
            const h = Math.round(rect.height)
            if (!w || !h) return
            if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
                canvas.width = w * dpr
                canvas.height = h * dpr
            }
            canvas.style.width = `${w}px`
            canvas.style.height = `${h}px`
            const ctx = canvas.getContext('2d')
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
            ctx.clearRect(0, 0, w, h)
            drawPaths(
                ctx,
                [{ id: 'track', points: trackingState.pathPoints }],
                toPx,
                { ghostCursor: cursorRef.current, showIndices: true }
            )
        }
        rafRef.current = requestAnimationFrame(draw)
        return () => rafRef.current && cancelAnimationFrame(rafRef.current)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mirrored])

    const commit = (next) => {
        trackingState.pathPoints = next
    }
    const hitPoint = (norm) => {
        const cur = toPx(norm)
        const p = trackingState.pathPoints
        for (let i = 0; i < p.length; i++) {
            const pt = toPx(p[i])
            if (Math.hypot(pt.x - cur.x, pt.y - cur.y) < 16) return i
        }
        return -1
    }
    const finish = () => {
        // Drop the duplicate point the double-click's first press just added, so
        // the line ends where the user stopped (not on the double-click spot).
        const p = trackingState.pathPoints
        if (p.length >= 3) {
            const a = p[p.length - 1]
            const b = p[p.length - 2]
            if (Math.hypot(a.x - b.x, a.y - b.y) < 0.03) commit(p.slice(0, -1))
        }
        trackingState.pathDone = true
        dragRef.current = null
        cursorRef.current = null
    }
    const onDown = (e) => {
        const norm = fromPointer(e.clientX, e.clientY)
        if (!norm) return
        e.currentTarget.setPointerCapture?.(e.pointerId)
        // Dragging an existing point is always allowed (even after finishing).
        const hit = hitPoint(norm)
        if (hit >= 0) {
            dragRef.current = hit
            return
        }
        // Once the line is finished, empty-space clicks no longer add points.
        if (trackingState.pathDone) return
        commit([...trackingState.pathPoints, norm])
    }
    // Double-click finishes the line: no more points, no more ghost preview.
    const onDblClick = () => {
        if (trackingState.pathPoints.length >= 2) finish()
    }
    const onMove = (e) => {
        const norm = fromPointer(e.clientX, e.clientY)
        if (!norm) return
        // No ghost preview once the line is finished (nothing more to add).
        cursorRef.current = trackingState.pathDone ? null : norm
        if (dragRef.current != null) {
            const next = trackingState.pathPoints.slice()
            next[dragRef.current] = norm
            commit(next)
        }
    }
    const onUp = () => {
        dragRef.current = null
    }
    const onLeave = () => {
        cursorRef.current = null
    }

    // Only show/edit the path when line crossing is enabled.
    if (!trackingState.lineCrossing) return null

    return (
        <>
            <canvas ref={canvasRef} className={styles.trackCanvas} />
            <div
                className={styles.trackDrawLayer}
                onPointerDown={onDown}
                onPointerMove={onMove}
                onPointerUp={onUp}
                onPointerLeave={onLeave}
                onDoubleClick={onDblClick}
            />
        </>
    )
}

export default TrackingControls
